import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Eye, EyeOff, Camera, ChevronRight, Sparkles, Lock, X, Trash2, AlertTriangle, Share2, MessageCircle, Link2, Check } from 'lucide-react';
import axios from 'axios';
import TransformationCamera from './TransformationCamera';
import { useTranslation } from '../context/LanguageContext';
import { useUnits } from '../context/UnitsContext';

import { API_URL } from '../config/api';

const API = API_URL;

// Share action sheet component
const ShareSheet = ({ isOpen, onClose, imageUrl, imageType, onShareToFeed, isDark, t }) => {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [sharingWhatsApp, setSharingWhatsApp] = useState(false);
  
  if (!isOpen) return null;
  
  const shareText = t('dashboard.transformation.shareText');
  const sharePromo = t('share.discoverSlimmy');
  const fullImageUrl = imageUrl?.startsWith('/api') ? `${window.location.origin}${imageUrl}` : imageUrl;
  
  // Stamp the white Slimmy logo (bottom-right, with shadow) onto the image using Canvas
  const stampLogoOnImage = (imageBlob) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        
        // Draw original image
        ctx.drawImage(img, 0, 0);
        
        // Load logo
        const logo = new Image();
        logo.crossOrigin = 'anonymous';
        logo.onload = () => {
          // Logo size: ~60% of image width
          const logoW = img.width * 0.6;
          const logoH = logoW * (logo.height / logo.width);
          const margin = img.width * 0.03;
          const x = (img.width - logoW) / 2;
          const y = img.height - logoH - margin;
          
          // Draw shadow behind logo
          ctx.save();
          ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
          ctx.shadowBlur = 12;
          ctx.shadowOffsetX = 2;
          ctx.shadowOffsetY = 2;
          ctx.drawImage(logo, x, y, logoW, logoH);
          ctx.restore();
          
          // Draw logo again without shadow for crisp result
          ctx.drawImage(logo, x, y, logoW, logoH);
          
          canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
        };
        logo.onerror = () => {
          // Logo failed to load, return original
          canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
        };
        logo.src = '/logo_white.png';
      };
      img.onerror = () => resolve(imageBlob);
      img.src = URL.createObjectURL(imageBlob);
    });
  };

  const shareToWhatsApp = async () => {
    setSharingWhatsApp(true);
    try {
      // Download image as blob
      const response = await fetch(fullImageUrl);
      const originalBlob = await response.blob();
      
      // Stamp Slimmy logo on image
      const stampedBlob = await stampLogoOnImage(originalBlob);
      const file = new File([stampedBlob || originalBlob], 'slimmy-transformation.jpg', { type: 'image/jpeg' });
      
      const shareData = {
        text: `${shareText}\n\n${sharePromo}\nhttps://slimmy.app`,
        files: [file],
      };
      
      // Use native Web Share API with image file
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        onClose();
        return;
      }
      
      // Fallback: share without image (text only)
      if (navigator.share) {
        await navigator.share({
          text: `${shareText}\n\n${sharePromo}\nhttps://slimmy.app`,
        });
        onClose();
        return;
      }
      
      // Last fallback: WhatsApp URL scheme (no image, just text)
      const message = `${shareText}\n\n${sharePromo}\nhttps://slimmy.app`;
      window.location.href = `whatsapp://send?text=${encodeURIComponent(message)}`;
      onClose();
    } catch (err) {
      // User cancelled share or error - just close silently
      if (err.name !== 'AbortError') {
        console.warn('Share failed:', err);
      }
    } finally {
      setSharingWhatsApp(false);
    }
  };
  
  const copyLink = () => {
    navigator.clipboard.writeText(fullImageUrl || window.location.href);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
      onClose();
    }, 1500);
  };
  
  const handleShareToFeed = async () => {
    setSharing(true);
    await onShareToFeed(imageType);
    setSharing(false);
    onClose();
  };
  
  return createPortal(
    <div 
      className="fixed inset-0 z-[400] flex items-center justify-center p-6"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Centered popup with rounded corners */}
      <div 
        className={`relative w-full max-w-sm rounded-3xl shadow-2xl ${isDark ? 'bg-[#1a2332]' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className={`p-5 pb-3`}>
          <h3 className={`font-semibold text-center text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('dashboard.transformation.shareTitle')}
          </h3>
        </div>
        
        <div className="px-3 pb-1">
          {/* Share to Feed */}
          <button 
            onClick={handleShareToFeed}
            disabled={sharing}
            className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-colors ${
              isDark ? 'hover:bg-white/5 active:bg-white/10' : 'hover:bg-gray-50 active:bg-gray-100'
            }`}
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E8334A] to-[#F59E0B] flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {sharing ? t('common.publishing') : t('share.publishToFeed')}
              </p>
              <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('share.shareCommunity')}
              </p>
            </div>
          </button>
          
          {/* WhatsApp - shares image via native share sheet */}
          <button 
            onClick={shareToWhatsApp}
            disabled={sharingWhatsApp}
            className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-colors ${
              isDark ? 'hover:bg-white/5 active:bg-white/10' : 'hover:bg-gray-50 active:bg-gray-100'
            }`}
          >
            <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </div>
            <div className="text-left">
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {sharingWhatsApp ? t('common.loading', 'Caricamento...') : 'WhatsApp'}
              </p>
              <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('share.sendToFriends')}
              </p>
            </div>
          </button>
          
          {/* Copy Link */}
          <button 
            onClick={copyLink}
            className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-colors ${
              isDark ? 'hover:bg-white/5 active:bg-white/10' : 'hover:bg-gray-50 active:bg-gray-100'
            }`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
              {copied ? (
                <Check className="w-5 h-5 text-green-500" />
              ) : (
                <Link2 className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-600'}`} />
              )}
            </div>
            <div className="text-left">
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {copied ? t('share.copied') : t('share.copyLink')}
              </p>
              <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('share.forEmailOrMessages')}
              </p>
            </div>
          </button>
        </div>
        
        <div className="px-4 pb-4 pt-2">
          <button 
            onClick={onClose}
            className={`w-full py-3 rounded-2xl font-medium transition-colors ${
              isDark 
                ? 'bg-white/10 text-white hover:bg-white/20' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Image with skeleton placeholder
const ImageWithSkeleton = ({ src, alt, className, onClick, isDark }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  return (
    <div className={`relative ${className}`} onClick={onClick}>
      {/* Skeleton placeholder */}
      {!loaded && !error && (
        <div className={`absolute inset-0 ${isDark ? 'bg-white/10' : 'bg-gray-200'} animate-pulse`} />
      )}
      {/* Actual image */}
      <img 
        src={src} 
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        loading="eager"
      />
    </div>
  );
};

// Fullscreen image modal with swipe between before/after
const ImageModal = ({ imageUrl, imageType, originalUrl, transformedUrl, onClose, onShare, isDark, t }) => {
  const [currentType, setCurrentType] = useState(imageType);
  const startXRef = React.useRef(0);
  const startYRef = React.useRef(0);
  const isDraggingRef = React.useRef(false);
  
  // Sync currentType when imageType prop changes (when modal opens)
  React.useEffect(() => {
    setCurrentType(imageType);
  }, [imageType]);
  
  // Touch events
  const handleTouchStart = (e) => {
    startXRef.current = e.touches[0].clientX;
    startYRef.current = e.touches[0].clientY;
    isDraggingRef.current = true;
  };
  
  const handleTouchMove = (e) => {
    if (!isDraggingRef.current) return;
    
    const diffX = Math.abs(startXRef.current - e.touches[0].clientX);
    const diffY = Math.abs(startYRef.current - e.touches[0].clientY);
    
    if (diffX > diffY && diffX > 10) {
      e.preventDefault();
    }
  };
  
  const handleTouchEnd = (e) => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    
    const endX = e.changedTouches[0].clientX;
    const diffX = startXRef.current - endX;
    const diffY = Math.abs(startYRef.current - e.changedTouches[0].clientY);
    
    handleSwipe(diffX, diffY);
  };
  
  // Mouse events
  const handleMouseDown = (e) => {
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    isDraggingRef.current = true;
  };
  
  const handleMouseUp = (e) => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    
    const diffX = startXRef.current - e.clientX;
    const diffY = Math.abs(startYRef.current - e.clientY);
    
    handleSwipe(diffX, diffY);
  };
  
  const handleSwipe = (diffX, diffY) => {
    if (Math.abs(diffX) > 50 && Math.abs(diffX) > diffY) {
      if (diffX > 0 && transformedUrl && originalUrl) {
        setCurrentType(prev => prev === 'original' ? 'transformed' : 'original');
      } else if (diffX < 0 && originalUrl && transformedUrl) {
        setCurrentType(prev => prev === 'transformed' ? 'original' : 'transformed');
      }
    }
  };
  
  // Hide bottom nav when modal is open
  React.useEffect(() => {
    if (imageUrl) {
      document.body.classList.add('modal-open-hide-nav');
    }
    return () => {
      document.body.classList.remove('modal-open-hide-nav');
    };
  }, [imageUrl]);

  if (!imageUrl) return null;
  
  return createPortal(
    <div 
      className="fixed inset-0 z-[300] bg-black"
    >
      {/* Swipeable Image Container - both images preloaded, toggle visibility */}
      <div
        className="absolute inset-0 flex items-center justify-center cursor-grab active:cursor-grabbing"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { isDraggingRef.current = false; }}
        style={{ touchAction: 'none' }}
      >
        {/* Original image - always in DOM, hidden when not active */}
        {originalUrl && (
          <img 
            src={originalUrl} 
            alt={t('dashboard.transformation.before')} 
            className={`absolute inset-0 w-full h-full object-cover select-none ${currentType === 'original' ? 'opacity-100' : 'opacity-0'}`}
            draggable={false}
            style={{ pointerEvents: 'none' }}
          />
        )}
        {/* Transformed image - always in DOM, hidden when not active */}
        {transformedUrl && (
          <img 
            src={transformedUrl} 
            alt={t('dashboard.transformation.after')} 
            className={`absolute inset-0 w-full h-full object-cover select-none ${currentType === 'transformed' ? 'opacity-100' : 'opacity-0'}`}
            draggable={false}
            style={{ pointerEvents: 'none' }}
          />
        )}
      </div>
      
      {/* Top gradient */}
      <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
      
      {/* Bottom gradient */}
      <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
      
      {/* Close button - with safe area */}
      <button 
        onClick={onClose}
        className="absolute left-4 w-12 h-12 rounded-full bg-[#E8334A] flex items-center justify-center text-white shadow-lg active:scale-95 transition-transform z-20"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
      >
        <X size={24} />
      </button>
      
      {/* Share button - with safe area */}
      <button 
        onClick={() => onShare(currentType === 'original' ? originalUrl : transformedUrl, currentType)}
        className="absolute right-4 w-12 h-12 rounded-full bg-[#E8334A] flex items-center justify-center text-white shadow-lg active:scale-95 transition-transform z-20"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
      >
        <Share2 size={22} />
      </button>
      
      {/* Navigation dots - clickable - with safe area */}
      {originalUrl && transformedUrl && (
        <div 
          className="absolute left-0 right-0 flex justify-center gap-4 z-10"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 80px)' }}
        >
          <button 
            onClick={() => setCurrentType('original')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              currentType === 'original' 
                ? 'bg-white text-black' 
                : 'bg-white/20 text-white/70'
            }`}
          >
            {t('dashboard.transformation.before')}
          </button>
          <button 
            onClick={() => setCurrentType('transformed')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              currentType === 'transformed' 
                ? 'bg-white text-black' 
                : 'bg-white/20 text-white/70'
            }`}
          >
            {t('dashboard.transformation.after')}
          </button>
        </div>
      )}
      
      {/* Footer */}
      <div className="absolute bottom-8 left-0 right-0 text-center z-10 pointer-events-none">
        <span className="inline-block px-5 py-2.5 rounded-full bg-black/50 backdrop-blur text-white font-medium">
          {currentType === 'transformed' ? `✨ ${t('dashboard.transformation.yourGoal')}` : `📸 ${t('dashboard.transformation.currentPhoto')}`}
        </span>
        {originalUrl && transformedUrl && (
          <p className="text-white/50 text-xs mt-2">
            {t('dashboard.transformation.swipeToChange')}
          </p>
        )}
      </div>
    </div>,
    document.body
  );
};

// Custom confirmation modal
const ConfirmDeleteModal = ({ isOpen, onConfirm, onCancel, isDark, t }) => {
  if (!isOpen) return null;
  
  return createPortal(
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div 
        className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#1a2332]' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
        </div>
        
        <h3 className={`text-xl font-bold text-center mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('dashboard.transformation.deleteTitle')}
        </h3>
        
        <p className={`text-center mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
          {t('dashboard.transformation.deleteConfirm')}
        </p>
        
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className={`flex-1 py-3 rounded-xl font-semibold transition-colors ${
              isDark 
                ? 'bg-white/10 text-white hover:bg-white/20' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors"
          >
            {t('dashboard.transformation.deleteBtn')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Get cached transformation synchronously
const getCachedTransformation = () => {
  try {
    const cached = localStorage.getItem('transformation_cache');
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      // Cache valid for 10 minutes
      if (data && Date.now() - timestamp < 10 * 60 * 1000) {
        return data;
      }
    }
  } catch (e) {}
  return null;
};

export default function TransformationCard({ user, isDark }) {
  const { t } = useTranslation();
  const { formatNumber, convertWeight, weightUnit } = useUnits();
  // Initialize from cache IMMEDIATELY
  const cachedData = getCachedTransformation();
  const [transformation, setTransformation] = useState(cachedData?.transformation || null);
  const [hidden, setHidden] = useState(cachedData?.hidden || false);
  const [goalWeight, setGoalWeight] = useState(cachedData?.goal_weight || null);
  const [currentWeight, setCurrentWeight] = useState(cachedData?.current_weight || null);
  const [canGenerate, setCanGenerate] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [loading, setLoading] = useState(!cachedData); // No loading if cache exists
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [enlargedImageType, setEnlargedImageType] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState(null);
  const [shareImageType, setShareImageType] = useState(null);
  const [toast, setToast] = useState(null);
  const [jobPhase, setJobPhase] = useState(null); // 'queued' | 'processing' (ARQ-level detail during pending)

  useEffect(() => {
    loadTransformation();
    checkCanGenerate();
  }, []);

  // Poll for transformation status when pending — adaptive interval (2s first 30s, then 4s, timeout at 180s)
  useEffect(() => {
    if (transformation?.status !== 'pending') return;

    const transformationId = transformation.transformation_id;
    if (!transformationId) return;

    let cancelled = false;
    let timeoutId = null;
    const pollStart = Date.now();
    const TIMEOUT_MS = 180_000; // 3 min hard cap (matches ARQ job_timeout)

    const poll = async () => {
      if (cancelled) return;
      const elapsed = Date.now() - pollStart;

      if (elapsed > TIMEOUT_MS) {
        // Client-side safety net — in practice ARQ will mark the job failed first
        showToast(t('dashboard.transformation.failed') || 'Trasformazione fallita', 'error');
        await loadTransformation(); // one last fetch to sync backend state
        return;
      }

      try {
        const res = await axios.get(
          `${API}/api/transformation/status/${transformationId}`,
          { withCredentials: true }
        );
        const newStatus = res.data?.status;
        // Track ARQ phase (queued vs processing) for UX feedback
        if (res.data?.job_phase) setJobPhase(res.data.job_phase);
        if (newStatus && newStatus !== 'pending') {
          if (!cancelled) {
            setJobPhase(null);
            // Full reload to refresh goal_weight etc.
            await loadTransformation();
            if (newStatus === 'completed') {
              showToast(t('dashboard.transformation.ready') || 'Trasformazione pronta! ✨', 'success');
            } else if (newStatus === 'failed') {
              showToast(t('dashboard.transformation.failed') || 'Trasformazione fallita', 'error');
            }
          }
          return;
        }
      } catch (err) {
        // 404 = transformation disappeared (user deleted during polling) → stop
        if (err?.response?.status === 404) return;
        console.warn('Transformation status poll error:', err?.message);
      }

      // Adaptive interval: fast for first 30s, slower after
      const nextDelay = elapsed < 30_000 ? 2000 : 4000;
      timeoutId = setTimeout(poll, nextDelay);
    };

    // First poll after 1.5s so the pending state has time to persist
    timeoutId = setTimeout(poll, 1500);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformation?.status, transformation?.transformation_id, t]);

  const loadTransformation = async () => {
    try {
      const response = await axios.get(`${API}/api/transformation/my`, { withCredentials: true });
      setTransformation(response.data.transformation);
      setHidden(response.data.hidden);
      setGoalWeight(response.data.goal_weight);
      setCurrentWeight(response.data.current_weight);
      
      // Save to cache
      try {
        localStorage.setItem('transformation_cache', JSON.stringify({
          data: {
            transformation: response.data.transformation,
            hidden: response.data.hidden,
            goal_weight: response.data.goal_weight,
            current_weight: response.data.current_weight
          },
          timestamp: Date.now()
        }));
      } catch (e) {}
    } catch (err) {
      console.error('Error loading transformation:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkCanGenerate = async () => {
    try {
      const response = await axios.get(`${API}/api/transformation/can-generate`, { withCredentials: true });
      setCanGenerate(response.data);
    } catch (err) {
      console.error('Error checking can generate:', err);
    }
  };

  const toggleVisibility = async () => {
    try {
      const response = await axios.put(
        `${API}/api/transformation/visibility`,
        { hidden: !hidden },
        { withCredentials: true }
      );
      setHidden(response.data.hidden);
    } catch (err) {
      console.error('Error toggling visibility:', err);
    }
  };

  const handleTransformationComplete = (newTransformation) => {
    setTransformation(newTransformation);
    setShowCamera(false);
    checkCanGenerate();
  };

  const deleteTransformation = async () => {
    setDeleting(true);
    setShowDeleteConfirm(false);
    try {
      await axios.delete(`${API}/api/transformation/my`, { withCredentials: true });
      setTransformation(null);
      // Clear the localStorage cache too — otherwise navigating away
      // and back briefly shows the deleted image again on remount,
      // because the component reads from cache before the API resolves.
      try {
        localStorage.removeItem('transformation_cache');
      } catch (e) {}
      checkCanGenerate();
    } catch (err) {
      console.error('Error deleting transformation:', err);
    } finally {
      setDeleting(false);
    }
  };

  const openShareSheet = (imageUrl, imageType) => {
    setShareImageUrl(imageUrl);
    setShareImageType(imageType);
    setShowShareSheet(true);
  };

  const handleShareToFeed = async (imageType) => {
    try {
      const imageUrl = imageType === 'transformed' 
        ? transformation.transformed_image_url 
        : transformation.original_image_url;
      
      // Create a post with the transformation image
      await axios.post(
        `${API}/api/posts`,
        {
          post_type: 'traguardo',
          text: t('dashboard.transformation.shareText') + '\n\n' + t('dashboard.transformation.shareGoalText', { weight: formatNumber(convertWeight(goalWeight), 1) + ' ' + weightUnit }),
          image_url: imageUrl,
          data: {
            type: 'transformation',
            image_type: imageType,
            goal_weight: goalWeight
          }
        },
        { withCredentials: true }
      );
      
      // Close modals and show success toast
      setEnlargedImage(null);
      setEnlargedImageType(null);
      showToast(t('feed.postPublished'), 'success');
    } catch (err) {
      console.error('Error sharing to feed:', err);
      showToast(t('dashboard.transformation.publishError'), 'error');
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleImageClick = (imageUrl, imageType) => {
    setEnlargedImage(imageUrl);
    setEnlargedImageType(imageType);
  };

  if (loading) {
    return (
      <div className={`rounded-2xl p-4 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
        <div className="animate-pulse">
          <div className={`h-4 ${isDark ? 'bg-white/10' : 'bg-gray-200'} rounded w-1/3 mb-4`}></div>
          <div className={`h-32 ${isDark ? 'bg-white/10' : 'bg-gray-200'} rounded`}></div>
        </div>
      </div>
    );
  }

  // No goal weight set
  if (!goalWeight) {
    return (
      <div className={`rounded-2xl p-4 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('dashboard.transformation.yourGoal')}
            </h3>
          </div>
        </div>
        <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'} mb-3`}>
          {t('dashboard.transformation.setGoalHint')}
        </p>
        <button
          onClick={() => window.location.href = '/profilo'}
          className={`w-full py-2.5 rounded-xl text-sm font-medium transition-colors ${
            isDark 
              ? 'bg-white/10 text-white hover:bg-white/20' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {t('dashboard.transformation.setGoalBtn')}
        </button>
      </div>
    );
  }

  // Has transformation
  if (transformation) {
    const weightToLose = currentWeight && goalWeight ? currentWeight - goalWeight : transformation.weight_to_lose;
    
    // Transformation is still processing
    if (transformation.status === 'pending') {
      return (
        <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between p-4 pb-2">
            <div className="flex items-center gap-2">
              <Sparkles className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('dashboard.transformation.yourGoal')}
              </h3>
            </div>
          </div>
          <div className="px-4 pb-4">
            <div className={`rounded-xl p-6 text-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
              <div className="relative w-16 h-16 mx-auto mb-3">
                <div className={`absolute inset-0 rounded-full border-4 border-t-amber-500 ${isDark ? 'border-white/10' : 'border-gray-200'} animate-spin`}></div>
                <Sparkles className={`absolute inset-0 m-auto w-6 h-6 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
              </div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {jobPhase === 'queued'
                  ? (t('dashboard.transformation.inQueue') || 'In coda…')
                  : (t('dashboard.transformation.creating'))}
              </p>
              <p className={`text-sm mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {jobPhase === 'queued'
                  ? (t('dashboard.transformation.queueWait') || 'Un worker la prenderà a breve')
                  : (t('dashboard.transformation.notificationReady'))}
              </p>
              <p className={`text-xs mt-3 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                {t('dashboard.transformation.continueUsing')}
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    // Transformation failed
    if (transformation.status === 'failed') {
      return (
        <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between p-4 pb-2">
            <div className="flex items-center gap-2">
              <Sparkles className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('dashboard.transformation.yourGoal')}
              </h3>
            </div>
          </div>
          <div className="px-4 pb-4">
            <div className={`rounded-xl p-6 text-center ${isDark ? 'bg-red-500/10' : 'bg-red-50'}`}>
              <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${isDark ? 'bg-red-500/20' : 'bg-red-100'}`}>
                <X className={`w-6 h-6 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
              </div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('dashboard.transformation.somethingWrong')}
              </p>
              <p className={`text-sm mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {transformation.error || t('dashboard.transformation.generationError')}
              </p>
              <button
                onClick={deleteTransformation}
                disabled={deleting}
                className="mt-4 px-4 py-2 rounded-xl text-sm font-medium bg-[#E8334A] text-white"
              >
                {deleting ? t('dashboard.transformation.deleting') : t('dashboard.transformation.retry')}
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <>
        <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 pb-2">
            <div className="flex items-center gap-2">
              <Sparkles className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('dashboard.transformation.yourGoal')}
              </h3>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
                className={`p-2 rounded-full transition-colors ${
                  isDark ? 'hover:bg-red-500/20' : 'hover:bg-red-50'
                }`}
                title="Elimina foto"
              >
                <Trash2 className={`w-5 h-5 ${deleting ? 'animate-spin' : ''} ${isDark ? 'text-red-400' : 'text-red-500'}`} />
              </button>
              <button
                onClick={toggleVisibility}
                className={`p-2 rounded-full transition-colors ${
                  isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'
                }`}
              >
                {hidden ? (
                  <EyeOff className={`w-5 h-5 ${isDark ? 'text-white/50' : 'text-gray-400'}`} />
                ) : (
                  <Eye className={`w-5 h-5 ${isDark ? 'text-white/50' : 'text-gray-400'}`} />
                )}
              </button>
            </div>
          </div>

          {/* Content */}
          {hidden ? (
            <div className="px-4 pb-4">
              <div className={`rounded-xl p-6 text-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                <EyeOff className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
                <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                  Foto nascosta
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-white/30' : 'text-gray-300'}`}>
                  Tocca l'icona occhio per mostrare
                </p>
              </div>
            </div>
          ) : (
            <div className="px-4 pb-4">
              {/* Before / After comparison */}
              <div className="flex gap-3 mb-3">
                {/* Today - Original photo */}
                <div className="flex-1">
                  <p className={`text-xs text-center mb-1 ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                    {t('dashboard.transformation.today')}
                  </p>
                  <div 
                    className="aspect-[3/4] rounded-xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
                    onClick={() => transformation.original_image_url && handleImageClick(transformation.original_image_url, 'original')}
                  >
                    {transformation.original_image_url ? (
                      <ImageWithSkeleton 
                        src={transformation.original_thumb_url || transformation.original_image_url} 
                        alt={t('dashboard.transformation.currentPhoto')} 
                        className="w-full h-full"
                        isDark={isDark}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-500/20 to-gray-600/20 flex items-center justify-center">
                        <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                          {currentWeight ? `${formatNumber(convertWeight(currentWeight), 1)} ${weightUnit}` : '—'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex items-center">
                  <ChevronRight className={`w-6 h-6 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
                </div>

                {/* Goal - Transformed photo */}
                <div className="flex-1">
                  <p className={`text-xs text-center mb-1 ${isDark ? 'text-amber-400' : 'text-amber-500'}`}>
                    -{formatNumber(convertWeight(weightToLose), 1)} {weightUnit.toUpperCase()}
                  </p>
                  <div 
                    className="aspect-[3/4] rounded-xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
                    onClick={() => handleImageClick(transformation.transformed_image_url, 'transformed')}
                  >
                    <ImageWithSkeleton 
                      src={transformation.transformed_thumb_url || transformation.transformed_image_url} 
                      alt={t('dashboard.transformation.yourGoal')} 
                      className="w-full h-full"
                      isDark={isDark}
                    />
                  </div>
                </div>
              </div>

              {/* Progress text */}
              <p className={`text-center text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {t('dashboard.transformation.goal')}: <span className="font-semibold">{formatNumber(convertWeight(goalWeight), 1)} {weightUnit}</span>
                {weightToLose > 0 && (
                  <> • {t('dashboard.transformation.remaining')} <span className="font-semibold">{formatNumber(convertWeight(weightToLose), 1)} {weightUnit}</span></>
                )}
              </p>

              {/* Retake photo button */}
              {canGenerate?.can_generate && (
                <button
                  onClick={() => setShowCamera(true)}
                  className={`w-full mt-3 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    isDark 
                      ? 'bg-white/10 text-white hover:bg-white/20' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Camera size={16} />
                  Scatta nuova foto
                </button>
              )}
            </div>
          )}
        </div>

        {/* Camera Modal */}
        {showCamera && (
          <TransformationCamera
            onComplete={handleTransformationComplete}
            onCancel={() => setShowCamera(false)}
          />
        )}

        {/* Fullscreen Image Modal */}
        <ImageModal 
          imageUrl={enlargedImage}
          imageType={enlargedImageType}
          originalUrl={transformation?.original_image_url}
          transformedUrl={transformation?.transformed_image_url}
          onClose={() => {
            setEnlargedImage(null);
            setEnlargedImageType(null);
          }}
          onShare={openShareSheet}
          isDark={isDark}
          t={t}
        />

        {/* Share Sheet */}
        <ShareSheet
          isOpen={showShareSheet}
          onClose={() => setShowShareSheet(false)}
          imageUrl={shareImageUrl}
          imageType={shareImageType}
          onShareToFeed={handleShareToFeed}
          isDark={isDark}
          t={t}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmDeleteModal
          isOpen={showDeleteConfirm}
          onConfirm={deleteTransformation}
          onCancel={() => setShowDeleteConfirm(false)}
          isDark={isDark}
          t={t}
        />
      </>
    );
  }

  // No transformation yet - show CTA
  return (
    <>
      <div className={`rounded-2xl p-5 ${isDark ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/10 border border-amber-500/30' : 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200'}`}>
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-amber-500/30' : 'bg-amber-100'}`}>
            <Sparkles className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
          </div>
          <div>
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('dashboard.transformation.seeGoal')}
            </h3>
            <p className={`text-xs ${isDark ? 'text-amber-400/80' : 'text-amber-600'}`}>
              Powered by AI
            </p>
          </div>
        </div>
        
        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'} mb-4`}>
          {t('dashboard.transformation.subtitle')} <strong className={isDark ? 'text-amber-400' : 'text-amber-600'}>{formatNumber(convertWeight(goalWeight), 1)} {weightUnit}</strong>!
        </p>

        {canGenerate?.can_generate ? (
          <button
            onClick={() => setShowCamera(true)}
            data-testid="transformation-photo-btn"
            className="w-full py-4 rounded-xl bg-gradient-to-r from-[#E8334A] to-[#F59E0B] text-white font-bold text-lg flex items-center justify-center gap-3 shadow-lg shadow-orange-500/30 active:scale-[0.98] transition-transform"
          >
            <Camera size={22} />
            {t('dashboard.transformation.takePhoto')}
          </button>
        ) : canGenerate?.limit_type === 'disabled' ? (
          <div className={`text-center py-4 px-4 rounded-xl ${isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
            <AlertTriangle className={`w-5 h-5 mx-auto mb-2 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
            <p className={`text-sm font-medium ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
              {t('dashboard.transformation.featureDisabled')}
            </p>
            <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
              {t('dashboard.transformation.featureDisabledSub')}
            </p>
          </div>
        ) : (
          <div className={`text-center py-3 px-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
            <Lock className={`w-5 h-5 mx-auto mb-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
            <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {canGenerate?.is_premium 
                ? `${t('common.days')}: ${7 - (canGenerate?.limit - canGenerate?.remaining)}`
                : t('premium.features.aiTransform')
              }
            </p>
          </div>
        )}

        {/* Premium badge if free */}
        {canGenerate && !canGenerate.is_premium && canGenerate.remaining > 0 && (
          <p className={`text-xs text-center mt-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
            {t('dashboard.transformation.freeRemaining', { count: canGenerate.remaining })}
          </p>
        )}
      </div>

      {/* Camera Modal */}
      {showCamera && (
        <TransformationCamera
          onComplete={handleTransformationComplete}
          onCancel={() => setShowCamera(false)}
        />
      )}

      {/* Custom Toast Notification */}
      {toast && createPortal(
        <div className="fixed top-4 left-4 right-4 flex justify-center pointer-events-none" style={{ zIndex: 99999 }}>
          <div 
            className={`
              px-6 py-4 rounded-2xl shadow-2xl pointer-events-auto
              flex items-center gap-3 animate-slide-down
              ${toast.type === 'success' 
                ? 'bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white' 
                : 'bg-gradient-to-r from-[#E8334A] to-[#dc2626] text-white'
              }
            `}
          >
            {toast.type === 'success' ? (
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : (
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <X size={20} />
              </div>
            )}
            <span className="font-semibold">{toast.message}</span>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
